import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import { composeEvent } from '@icp/utils';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import BigNumber from 'bignumber.js';
import { FormHelperTextMaterial } from '../../FormHelperText';

function isTypedNumber(newValue) {
  const newValueNumber = Number(newValue);
  return !Number.isNaN(newValueNumber) && String(newValueNumber).length === newValue.length;
}

// ant 支持 precision 属性，material ui 不支持，这里自定义支持让 material 行为和 ant 保持一致。
function toPrecision(newValue, precision) {
  if (!precision || typeof precision !== 'number') {
    return newValue;
  }
  const x = new BigNumber(newValue);
  return x.toFixed(precision);
}

const NumberPickerMaterial = forwardRef(function NumberPickerMaterial(props, ref) {
  const {
    id,
    title,
    value,
    disabled,
    readonly,
    componentProps,
    validation,
    status,
    helpers,
    onChange,
    onTouchChanged,
    onBlur,
    precision,
    readonlyText,
    // min, max, step, precision, formatOptions
  } = props;

  const [tempValue, setTempValue] = useState(value);
  const { changeDelayToBlur, stringMode, ...otherComponentProps } = componentProps;

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const triggerChange = (newValue) => {
    if (stringMode) {
      onChange(toPrecision(newValue, precision));
    } else if (isTypedNumber(newValue)) {
      onChange(Number(toPrecision(newValue, precision)));
    } else {
      // 不合法，恢复成 props.value
      setTempValue(value);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setTempValue(newValue);

    if (!changeDelayToBlur) {
      triggerChange(newValue);
    }
  };

  const handleBlur = (event) => {
    const newValue = event.target.value;
    setTempValue(newValue);

    if (changeDelayToBlur) {
      triggerChange(newValue);
    }

    if (onTouchChanged) {
      onTouchChanged();
    }
  };

  const InputProps = useMemo(() => {
    const obj = { readOnly: readonly };
    // 必须这么单独指定一下，obj 不能有 value 属性，undefined 都不行，否则都会让 TextField 的 value 值失效
    if (readonly) {
      obj.value = readonlyText;
    }
    return obj;
  }, [readonly, readonlyText]);

  return (
    <TextField
      // 当 readonly 的时候，需要在 InputProps.value 里直接指定显示文字，type 不能是 number，而是 string
      type={readonly ? 'text' : 'number'}
      {...otherComponentProps}
      fullWidth={true}
      id={id}
      label={title}
      value={tempValue ?? ''}
      inputMode="numeric"
      error={status === 'error'}
      helperText={!helpersIsEmpty(helpers) ? <FormHelperTextMaterial helpers={helpers} /> : null}
      required={validation?.required}
      disabled={disabled}
      InputProps={InputProps}
      onChange={handleChange}
      onBlur={composeEvent(handleBlur, onBlur)}
      ref={ref}
    />
  );
});

NumberPickerMaterial.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  componentProps: PropTypes.shape({
    changeDelayToBlur: PropTypes.bool,
    stringMode: PropTypes.bool,
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,

  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onTouchChanged: PropTypes.func,
  precision: PropTypes.number,
  readonlyText: PropTypes.string,
};

export default NumberPickerMaterial;
